import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAccountBalance, payForResumeAnalysis, checkResumeAnalysisPayment, requestProfessionalResumeReview } from "../../../redux/actions/walletActions";
import { numberWithCommas } from "../../../utils/helpers";
import "./ResumeAnalysisModal.css";

const ANALYSIS_COST_USD = 29.00;
const ANALYSIS_COST_NGN = 7999.00;

const ResumeAnalysisModal = ({ isOpen, onClose, analysisData, requiresPayment = true }) => {
  const dispatch = useDispatch();
  const [hasPaid, setHasPaid] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const contentRef = useRef(null);
  const blurredRef = useRef(null);
  const paywallTriggered = useRef(false);
  const { balance } = useSelector((state) => state.wallet);
  const { userData } = useSelector((state) => state.auth);

  // Custom toast state
  const [modalToast, setModalToast] = useState({ show: false, message: "", type: "info" });

  // Custom toast function
  const showModalToast = (type = "info", message, duration = 4000) => {
    setModalToast({ show: true, message, type });
    setTimeout(() => {
      setModalToast({ show: false, message: "", type: "info" });
    }, duration);
  };

  const isNigerian = userData?.country === "160";
  const analysisCost = isNigerian ? ANALYSIS_COST_NGN : ANALYSIS_COST_USD;
  const currencySymbol = isNigerian ? "\u20A6" : "$";
  const currencyLabel = isNigerian ? "NGN" : "USD";

  useEffect(() => {
    if (isOpen) {
      if (requiresPayment) {
        getAccountBalance(dispatch);
        // Check if user already paid for resume analysis
        setCheckingPayment(true);
        checkResumeAnalysisPayment()
          .then((res) => {
            if (res.has_paid) {
              setHasPaid(true);
              setShowPaywall(false);
            }
          })
          .finally(() => setCheckingPayment(false));
      } else {
        // If payment not required, set hasPaid to true to show full content
        setHasPaid(true);
        setShowPaywall(false);
      }
    }
  }, [isOpen, dispatch, requiresPayment]);

  const getCurrentBalance = () => {
    if (!balance || !balance[0]) return 0;
    return parseFloat(balance[0].available);
  };

  const handleScroll = useCallback(() => {
    if (!requiresPayment || hasPaid || paywallTriggered.current || !contentRef.current || !blurredRef.current) return;
    const containerRect = contentRef.current.getBoundingClientRect();
    const blurredRect = blurredRef.current.getBoundingClientRect();
    if (blurredRect.top < containerRect.bottom - 60) {
      paywallTriggered.current = true;
      setShowPaywall(true);
    }
  }, [hasPaid, requiresPayment]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isOpen]);

  useEffect(() => {
    if (isOpen) {
      paywallTriggered.current = false;
      setShowPaywall(false);
    }
  }, [isOpen]);

  if (!isOpen || !analysisData) return null;

  const {
    job_family,
    job_level,
    overall_score,
    sectional_grades,
    top_fixes,
    rewrite_suggestions,
    semantic_recommendations,
    ats_findings,
  } = analysisData;

  // Function to download the analysis as a styled PDF using jsPDF
  const handleDownload = () => {
    if (!hasPaid) {
      setShowPaywall(true);
      return;
    }

    const { jsPDF } = window.jspdf;
    if (!jsPDF) {
      showModalToast("error", "PDF library not loaded. Please refresh and try again.");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = 0;

    const checkPageBreak = (needed) => {
      if (y + needed > pageHeight - 15) {
        doc.addPage();
        y = 15;
      }
    };

    // ========== BLUE HEADER BAR ==========
    doc.setFillColor(58, 110, 212); // #3A6ED4
    doc.rect(0, 0, pageWidth, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Resume Analysis Report", margin + 2, 12);
    y = 24;

    // ========== SCORE CARD (gray bg like modal #6e7789) ==========
    const scoreCardH = 38;
    doc.setFillColor(110, 119, 137); // #6e7789
    doc.roundedRect(margin, y, contentWidth, scoreCardH, 4, 4, "F");

    // Score circle - orange ring (#f59e0b)
    const circleX = margin + 22;
    const circleY = y + scoreCardH / 2;
    const circleR = 14;

    // Outer orange ring
    doc.setDrawColor(245, 158, 11); // #f59e0b orange
    doc.setLineWidth(3);
    doc.circle(circleX, circleY, circleR, "S");

    // Inner dark fill
    doc.setFillColor(80, 88, 104);
    doc.circle(circleX, circleY, circleR - 2.5, "F");

    // Score number
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(`${overall_score}`, circleX, circleY + 1, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("/100", circleX, circleY + 6, { align: "center" });

    // Job Family & Level labels
    const labelX = margin + 45;
    doc.setTextColor(200, 205, 215); // light gray label
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("JOB FAMILY", labelX, y + 13);
    doc.text("JOB LEVEL", labelX + 45, y + 13);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(job_family || "N/A", labelX, y + 20);
    doc.text(job_level || "N/A", labelX + 45, y + 20);

    y += scoreCardH + 8;

    // ========== HELPER: Draw white section card ==========
    const drawSectionCard = (title, icon, drawContent) => {
      checkPageBreak(20);
      const startY = y;
      // We'll draw the card background after measuring content
      const cardX = margin;
      const cardW = contentWidth;

      // Title
      y += 8;
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 51, 51); // #333
      doc.text(`${icon}  ${title}`, cardX + 8, y);
      y += 8;

      // Draw content
      drawContent();

      y += 4;
      const cardH = y - startY;

      // Draw card background behind (white rounded rect)
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, startY, cardW, cardH, 3, 3, "F");

      // Light shadow border
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.roundedRect(cardX, startY, cardW, cardH, 3, 3, "S");

      // Re-draw title on top of background
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(51, 51, 51);
      doc.text(`${icon}  ${title}`, cardX + 8, startY + 8);

      return startY + 8; // return title Y for content offset
    };

    // ========== SECTIONAL GRADES ==========
    checkPageBreak(40);
    let cardStartY = y;
    const gradesEntries = Object.entries(sectional_grades || {});

    // Pre-calculate card height
    const gradeRows = Math.ceil(gradesEntries.length / 3);
    const gradeCardH = 16 + gradeRows * 16 + 6;

    // White card background
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, gradeCardH, 3, 3, "F");
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, gradeCardH, 3, 3, "S");

    // Title
    y += 10;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("Sectional Grades", margin + 8, y);
    y += 6;

    // Grade items in grid (3 per row)
    if (gradesEntries.length > 0) {
      const cols = 3;
      const itemW = (contentWidth - 16) / cols - 3;
      let gx = margin + 8;
      gradesEntries.forEach(([key, value], i) => {
        if (i > 0 && i % cols === 0) {
          y += 14;
          gx = margin + 8;
        }

        // Gradient gray background for grade item
        doc.setFillColor(245, 247, 250); // #f5f7fa
        doc.roundedRect(gx, y, itemW, 12, 2, 2, "F");

        // Grade label text
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(85, 85, 85); // #555
        const labelText = key.replace(/_/g, " ").toUpperCase();
        const truncLabel = labelText.length > 30 ? labelText.substring(0, 28) + ".." : labelText;
        doc.text(truncLabel, gx + 4, y + 7);

        // Purple badge for grade value (#667eea)
        const badgeW = 14;
        const badgeX = gx + itemW - badgeW - 3;
        doc.setFillColor(102, 126, 234); // #667eea
        doc.roundedRect(badgeX, y + 2, badgeW, 8, 4, 4, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text(`${value}`, badgeX + badgeW / 2, y + 7.5, { align: "center" });

        gx += itemW + 3;
      });
      y += 14;
    }
    y = cardStartY + gradeCardH + 6;

    // ========== TOP FIXES ==========
    checkPageBreak(25);
    cardStartY = y;
    const fixItems = top_fixes || [];
    const fixCardH = 14 + fixItems.length * 8 + 4;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, Math.max(fixCardH, 22), 3, 3, "F");
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, Math.max(fixCardH, 22), 3, 3, "S");

    y += 10;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("Top Fixes", margin + 8, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(85, 85, 85);
    if (fixItems.length > 0) {
      fixItems.forEach((fix) => {
        checkPageBreak(8);
        // Blue bullet dot
        doc.setFillColor(102, 126, 234); // #667eea
        doc.circle(margin + 10, y - 1, 1.2, "F");
        const lines = doc.splitTextToSize(fix, contentWidth - 22);
        doc.text(lines, margin + 15, y);
        y += lines.length * 4.5 + 3;
      });
    } else {
      doc.text("None", margin + 15, y);
      y += 6;
    }
    y += 4;

    // ========== REWRITE SUGGESTIONS ==========
    checkPageBreak(20);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("Rewrite Suggestions", margin + 8, y + 4);
    y += 10;

    if (rewrite_suggestions && rewrite_suggestions.length > 0) {
      rewrite_suggestions.forEach((s, i) => {
        // Calculate dynamic height based on text length
        const origLines = doc.splitTextToSize(s.original || "", (contentWidth - 24) / 2 - 8);
        const rewriteLines = doc.splitTextToSize(s.rewrite || "", (contentWidth - 24) / 2 - 8);
        const reasonLines = doc.splitTextToSize(`Reason: ${s.reason || ""}`, contentWidth - 20);
        const topH = Math.max(origLines.length, rewriteLines.length) * 4.5 + 16;
        const reasonH = reasonLines.length * 4.5 + 10;
        const cardH = topH + reasonH + 14;

        checkPageBreak(cardH);

        // Yellow/gold gradient card background (#ffeaa7 -> #fdcb6e)
        doc.setFillColor(255, 234, 167); // #ffeaa7
        doc.roundedRect(margin, y, contentWidth, cardH, 3, 3, "F");

        // Number badge
        doc.setFillColor(255, 255, 255, 200);
        doc.roundedRect(margin + 6, y + 4, 14, 7, 3, 3, "F");
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(225, 112, 85); // #e17055
        doc.text(`#${i + 1}`, margin + 13, y + 9, { align: "center" });

        // Original block (left)
        const blockY = y + 14;
        const halfW = (contentWidth - 24) / 2;

        // Original white box
        doc.setFillColor(255, 255, 255, 230);
        doc.roundedRect(margin + 6, blockY, halfW, topH - 6, 2, 2, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(102, 102, 102);
        doc.text("ORIGINAL", margin + 10, blockY + 5);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 51, 51);
        doc.text(origLines, margin + 10, blockY + 10);

        // Arrow
        doc.setFontSize(12);
        doc.setTextColor(150, 150, 150);
        doc.text("->", margin + 6 + halfW + 2, blockY + topH / 2 - 1);

        // Rewrite white box (right)
        const rightX = margin + 6 + halfW + 12;
        doc.setFillColor(255, 255, 255, 230);
        doc.roundedRect(rightX, blockY, halfW, topH - 6, 2, 2, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(102, 102, 102);
        doc.text("REWRITE", rightX + 4, blockY + 5);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 51, 51);
        doc.text(rewriteLines, rightX + 4, blockY + 10);

        // Reason box (bottom, with red-orange left border)
        const reasonY = blockY + topH;
        doc.setFillColor(255, 255, 255, 180);
        doc.roundedRect(margin + 6, reasonY, contentWidth - 12, reasonH, 2, 2, "F");
        doc.setDrawColor(225, 112, 85); // #e17055
        doc.setLineWidth(1);
        doc.line(margin + 6, reasonY, margin + 6, reasonY + reasonH);

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(225, 112, 85);
        doc.text("Reason:", margin + 10, reasonY + 5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 51, 51);
        doc.text(doc.splitTextToSize(s.reason || "", contentWidth - 30), margin + 24, reasonY + 5);

        y += cardH + 5;
      });
    } else {
      doc.setFontSize(9);
      doc.setTextColor(85, 85, 85);
      doc.text("None", margin + 15, y);
      y += 8;
    }
    y += 4;

    // ========== SEMANTIC RECOMMENDATIONS ==========
    checkPageBreak(25);
    cardStartY = y;
    const semItems = semantic_recommendations || [];
    const semCardH = 14 + semItems.length * 8 + 4;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, Math.max(semCardH, 22), 3, 3, "F");
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, Math.max(semCardH, 22), 3, 3, "S");

    y += 10;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("Semantic Recommendations", margin + 8, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(85, 85, 85);
    if (semItems.length > 0) {
      semItems.forEach((rec) => {
        checkPageBreak(8);
        doc.setFillColor(102, 126, 234);
        doc.circle(margin + 10, y - 1, 1.2, "F");
        const lines = doc.splitTextToSize(rec, contentWidth - 22);
        doc.text(lines, margin + 15, y);
        y += lines.length * 4.5 + 3;
      });
    } else {
      doc.text("None", margin + 15, y);
      y += 6;
    }
    y += 4;

    // ========== ATS COMPATIBILITY CHECK ==========
    checkPageBreak(30);
    cardStartY = y;

    // Calculate card height dynamically
    let atsContentH = 16;
    if (ats_findings) {
      atsContentH += 3 * 12; // 3 check items
      if (ats_findings.recommendations && ats_findings.recommendations.length > 0) {
        atsContentH += 12 + ats_findings.recommendations.length * 8;
      }
    }
    atsContentH += 6;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, atsContentH, 3, 3, "F");
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, contentWidth, atsContentH, 3, 3, "S");

    y += 10;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("ATS Compatibility Check", margin + 8, y);
    y += 8;

    if (ats_findings) {
      const atsItems = [
        { label: "Multi-Column Layout", issue: ats_findings.has_multi_column_layout, yesText: "Detected", noText: "Not Detected" },
        { label: "Contact Info Placement", issue: ats_findings.contact_info_placement_issue, yesText: "Issue Found", noText: "OK" },
        { label: "Non-Standard Characters", issue: ats_findings.non_standard_characters_detected, yesText: "Detected", noText: "Not Detected" },
      ];

      atsItems.forEach((item) => {
        checkPageBreak(12);
        // Green or orange background bar
        if (item.issue) {
          doc.setFillColor(254, 243, 226); // light orange bg
        } else {
          doc.setFillColor(220, 252, 231); // light green bg
        }
        doc.roundedRect(margin + 6, y - 3, contentWidth - 12, 10, 2, 2, "F");

        // Icon
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        if (item.issue) {
          doc.setTextColor(217, 119, 6); // #d97706 orange
          doc.text("!", margin + 12, y + 3);
        } else {
          doc.setTextColor(5, 150, 105); // #059669 green
          doc.text("V", margin + 12, y + 3);
        }

        // Label text
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        if (item.issue) {
          doc.setTextColor(217, 119, 6);
        } else {
          doc.setTextColor(5, 150, 105);
        }
        doc.text(`${item.label}: ${item.issue ? item.yesText : item.noText}`, margin + 20, y + 3);
        y += 12;
      });

      // ATS Recommendations box (purple/blue bg like modal)
      if (ats_findings.recommendations && ats_findings.recommendations.length > 0) {
        y += 2;
        checkPageBreak(20);
        const recsH = 10 + ats_findings.recommendations.length * 7;

        // Light purple background
        doc.setFillColor(235, 238, 252); // rgba(102,126,234,0.1)
        doc.roundedRect(margin + 6, y, contentWidth - 12, recsH, 2, 2, "F");

        // Purple left border
        doc.setDrawColor(102, 126, 234); // #667eea
        doc.setLineWidth(1.2);
        doc.line(margin + 6, y, margin + 6, y + recsH);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(102, 126, 234);
        doc.text("ATS Recommendations:", margin + 12, y + 6);
        let recY = y + 12;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(85, 85, 85);
        ats_findings.recommendations.forEach((rec) => {
          doc.setTextColor(102, 126, 234);
          doc.text("->", margin + 12, recY);
          doc.setTextColor(85, 85, 85);
          const lines = doc.splitTextToSize(rec, contentWidth - 36);
          doc.text(lines, margin + 20, recY);
          recY += lines.length * 4.5 + 3;
        });
        y += recsH + 4;
      }
    } else {
      doc.setFontSize(9);
      doc.setTextColor(85, 85, 85);
      doc.text("No ATS findings available.", margin + 15, y);
      y += 8;
    }

    // ========== FOOTER ==========
    y += 8;
    checkPageBreak(12);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + contentWidth, y);
    y += 5;
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.setFont("helvetica", "normal");
    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    doc.text(`Generated by workbrook  |  ${dateStr}`, pageWidth / 2, y, { align: "center" });

    doc.save(`resume-analysis-report-${Date.now()}.pdf`);
    showModalToast("success", "PDF downloaded successfully!");
  };

  // Function to get score color based on value
  const getScoreColor = (score) => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const handleRequestProfessionalReview = async () => {
    if (reviewRequested) {
      showModalToast("info", "You've already requested a professional review. Our team will contact you soon.");
      return;
    }

    setRequestingReview(true);
    try {
      const result = await requestProfessionalResumeReview({
        resume_score: overall_score,
        resume_id: analysisData?.resume_id || userData?.resume || null,
      });

      if (result.status) {
        showModalToast("success", "Request sent! Our team will contact you within 24-48 hours with a free professional report.", 5000);
        setReviewRequested(true);
      } else {
        showModalToast("error", result.message || "Failed to send request. Please try again.");
      }
    } catch (error) {
      showModalToast("error", "Failed to send request. Please try again.");
    } finally {
      setRequestingReview(false);
    }
  };

  const handlePayClick = async () => {
    const currentBalance = getCurrentBalance();
    if (currentBalance < analysisCost) {
      showModalToast("warn",
        `Insufficient balance (${currencySymbol}${numberWithCommas(currentBalance.toFixed(2))}). You need ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))}.`,
        4000
      );
      setTimeout(() => {
        showModalToast("info", "Redirecting to top up wallet so you can continue payment...", 3000);
      }, 800);
      setTimeout(() => {
        setShowPaywall(false);
        window.open("/wallet/add", "_blank");
      }, 2000);
      return;
    }

    setPaymentLoading(true);
    try {
      const result = await payForResumeAnalysis(dispatch, {
        analysis_type: "resume_analysis",
        resume_id: analysisData?.resume_id || userData?.resume || null,
      });

      if (result.type === "PAY_FOR_RESUME_ANALYSIS") {
        showModalToast("success", "Payment successful! Full report unlocked.");
        setHasPaid(true);
        setShowPaywall(false);
        getAccountBalance(dispatch);
        // Re-check payment status to ensure backend sync
        checkResumeAnalysisPayment().then((res) => {
          if (res.has_paid) {
            setHasPaid(true);
          }
        });
      } else {
        showModalToast("error", result.payload || "Payment failed. Please try again.");
      }
    } catch (error) {
      showModalToast("error", "Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div className="resume-analysis-overlay" onClick={onClose}></div>
      <div className="resume-analysis-modal">
        <div className="modal-header">
          <h2>Resume Analysis Report</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content" ref={contentRef}>

          {/* ===== FREE PREVIEW: Score Card ===== */}
          <div className="score-card">
            <div className="score-circle-container">
              <svg className="score-circle" viewBox="0 0 120 120">
                <circle
                  className="score-circle-bg"
                  cx="60"
                  cy="60"
                  r="54"
                ></circle>
                <circle
                  className="score-circle-progress"
                  cx="60"
                  cy="60"
                  r="54"
                  style={{
                    strokeDashoffset: 339.292 - (339.292 * overall_score) / 100,
                    stroke: getScoreColor(overall_score),
                  }}
                ></circle>
              </svg>
              <div className="score-text">
                <span className="score-value">{overall_score}</span>
                <span className="score-max">/100</span>
              </div>
            </div>
            <div className="score-details">
              <div className="detail-item">
                <span className="detail-label">Job Family</span>
                <span className="detail-value">{job_family}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Job Level</span>
                <span className="detail-value">{job_level}</span>
              </div>
            </div>
          </div>

          {/* ===== PROFESSIONAL REVIEW CTA (After Score) - Only show when user has paid ===== */}
          {hasPaid && (
            <div className="professional-review-cta">
              <div className="cta-content">
                <div className="cta-icon">👨‍💼</div>
                <div className="cta-text">
                  <h4>Want a Free Professional Review?</h4>
                  <p>Get expert feedback and a detailed professional report from our recruitment specialists.</p>
                </div>
              </div>
              <button 
                className={`cta-button ${reviewRequested ? 'requested' : ''}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                {requestingReview ? 'Sending...' : reviewRequested ? '✓ Request Sent' : 'Request Professional Review'}
              </button>
            </div>
          )}

          {/* ===== LOCKED CONTENT: Blurred when not paid ===== */}
          <div
            ref={blurredRef}
            className={requiresPayment && !hasPaid ? "locked-content" : ""}
            style={{ position: "relative" }}
          >
            {/* Sectional Grades */}
            <div className="section">
              <h3 className="section-title">Sectional Grades</h3>
              <div className="grades-grid">
                {Object.entries(sectional_grades || {}).map(([key, value]) => (
                  <div key={key} className="grade-item">
                    <div className="grade-label">
                      {key.replace(/_/g, " ").toUpperCase()}
                    </div>
                    <div className="grade-badge">{value}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Top Fixes */}
            {top_fixes && top_fixes.length > 0 && (
              <div className="section">
                <h3 className="section-title">
                  <span className="icon">🔧</span> Top Fixes
                </h3>
                <ul className="fixes-list">
                  {top_fixes.map((fix, idx) => (
                    <li key={idx} className="fix-item">
                      {fix}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rewrite Suggestions */}
            {rewrite_suggestions && rewrite_suggestions.length > 0 && (
              <div className="section">
                <h3 className="section-title">
                  <span className="icon">✍️</span> Rewrite Suggestions
                </h3>
                <div className="rewrite-suggestions">
                  {rewrite_suggestions.map((suggestion, idx) => (
                    <div key={idx} className="suggestion-card">
                      <div className="suggestion-header">
                        <span className="suggestion-number">#{idx + 1}</span>
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-block original">
                          <div className="block-label">Original</div>
                          <div className="block-text">{suggestion.original}</div>
                        </div>
                        <div className="arrow-icon">→</div>
                        <div className="suggestion-block rewrite">
                          <div className="block-label">Rewrite</div>
                          <div className="block-text">{suggestion.rewrite}</div>
                        </div>
                      </div>
                      <div className="suggestion-reason">
                        <strong>Reason:</strong> {suggestion.reason}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Semantic Recommendations */}
            {semantic_recommendations && semantic_recommendations.length > 0 && (
              <div className="section">
                <h3 className="section-title">
                  <span className="icon">💡</span> Semantic Recommendations
                </h3>
                <ul className="recommendations-list">
                  {semantic_recommendations.map((rec, idx) => (
                    <li key={idx} className="recommendation-item">
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ATS Findings */}
            {ats_findings && (
              <div className="section">
                <h3 className="section-title">
                  <span className="icon">🤖</span> ATS Compatibility Check
                </h3>
                <div className="ats-checks">
                  <div
                    className={`ats-check ${
                      ats_findings.has_multi_column_layout ? "warning" : "success"
                    }`}
                  >
                    <span className="check-icon">
                      {ats_findings.has_multi_column_layout ? "⚠️" : "✓"}
                    </span>
                    <span className="check-text">
                      Multi-Column Layout:{" "}
                      {ats_findings.has_multi_column_layout
                        ? "Detected"
                        : "Not Detected"}
                    </span>
                  </div>
                  <div
                    className={`ats-check ${
                      ats_findings.contact_info_placement_issue
                        ? "warning"
                        : "success"
                    }`}
                  >
                    <span className="check-icon">
                      {ats_findings.contact_info_placement_issue ? "⚠️" : "✓"}
                    </span>
                    <span className="check-text">
                      Contact Info Placement:{" "}
                      {ats_findings.contact_info_placement_issue
                        ? "Issue Found"
                        : "OK"}
                    </span>
                  </div>
                  <div
                    className={`ats-check ${
                      ats_findings.non_standard_characters_detected
                        ? "warning"
                        : "success"
                    }`}
                  >
                    <span className="check-icon">
                      {ats_findings.non_standard_characters_detected ? "⚠️" : "✓"}
                    </span>
                    <span className="check-text">
                      Non-Standard Characters:{" "}
                      {ats_findings.non_standard_characters_detected
                        ? "Detected"
                        : "Not Detected"}
                    </span>
                  </div>
                </div>
                {ats_findings.recommendations &&
                  ats_findings.recommendations.length > 0 && (
                    <div className="ats-recommendations">
                      <h4 className="ats-rec-title">ATS Recommendations:</h4>
                      <ul className="ats-rec-list">
                        {ats_findings.recommendations.map((rec, idx) => (
                          <li key={idx} className="ats-rec-item">
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>

        {/* ===== PAYWALL MODAL (appears when triggered) ===== */}
        {requiresPayment && showPaywall && !hasPaid && (
          <div className="paywall-overlay" onClick={() => setShowPaywall(false)}>
            <div className="paywall-modal" onClick={(e) => e.stopPropagation()}>
              <div className="paywall-icon">🔒</div>
              <h3 className="paywall-title">Unlock Full Report</h3>
              <p className="paywall-description">
                Get detailed fixes, rewrite suggestions, semantic recommendations,
                and ATS compatibility insights to improve your resume.
              </p>
              <ul className="paywall-features">
                <li>🔧 Top Fixes & Action Items</li>
                <li>✍️ AI-Powered Rewrite Suggestions</li>
                <li>💡 Semantic Recommendations</li>
                <li>🤖 Full ATS Compatibility Report</li>
                <li>⬇️ Downloadable Report</li>
              </ul>
              <div className="paywall-balance">
                <p>Your wallet balance: <strong>{currencySymbol}{numberWithCommas(getCurrentBalance().toFixed(2))}</strong></p>
                <p>Cost: <strong>{currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} {currencyLabel}</strong></p>
              </div>
              <button
                className="paywall-pay-btn"
                onClick={handlePayClick}
                disabled={paymentLoading}
              >
                {paymentLoading
                  ? "Processing..."
                  : getCurrentBalance() >= analysisCost
                  ? `Pay ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))} from Wallet`
                  : "Top Up Wallet"}
              </button>
              <button
                className="paywall-dismiss-btn"
                onClick={() => setShowPaywall(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {!requiresPayment || hasPaid ? (
            <>
              <button className="download-btn" onClick={handleDownload}>
                <span className="btn-icon">⬇</span>
                Download Report
              </button>
              <button 
                className={`professional-review-footer-btn ${reviewRequested ? 'requested' : ''}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                <span className="btn-icon">👨‍💼</span>
                {requestingReview ? 'Sending...' : reviewRequested ? '✓ Review Requested' : 'Request Professional Review'}
              </button>
              <button className="close-footer-btn" onClick={onClose}>
                Close
              </button>
            </>
          ) : (
            <>
              <button className="pay-footer-btn" onClick={() => setShowPaywall(true)} disabled={paymentLoading}>
                <span className="btn-icon">🔓</span>
                Pay {currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} to Download & View Full Report
              </button>
              <button className="close-footer-btn" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>

      {/* ===== CUSTOM TOAST NOTIFICATION (rendered outside modal so it appears at top) ===== */}
      {modalToast.show && (
        <div className={`modal-toast modal-toast-${modalToast.type}`}>
          <div className="modal-toast-content">
            <span className="modal-toast-icon">
              {modalToast.type === "success" ? "✓" :
               modalToast.type === "error" ? "✕" :
               modalToast.type === "warn" ? "⚠" : "ℹ"}
            </span>
            <span className="modal-toast-message">{modalToast.message}</span>
            <button
              className="modal-toast-close"
              onClick={() => setModalToast({ show: false, message: "", type: "info" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ResumeAnalysisModal;
